// Papel do viewer na org ativa, para telas que gateiam BOTÃO/ESCOPO de dado
// (não tela inteira — isso é canAccess). Lê o mesmo cache ["org-users"] que as
// telas usam; devolve a string do papel (ou null enquanto carrega/erro).
import type { PermissionCatalog } from "@/api";
import { usePlatformDirectorBrands } from "@/hooks/use-brands";
import { useAccessMe, usePermissionCatalog } from "@/hooks/use-roles";
import { useOrgUsers } from "@/hooks/use-users";
import { isDirector } from "@/lib/access";

export function useViewerRole(): string | null {
  const { data } = useOrgUsers();
  return data?.viewerRole ?? null;
}

export function useIsDirector(): boolean {
  return isDirector(useViewerRole());
}

// Diretor de PLATAFORMA — o time da LAI, não o diretor de uma marca. O back
// responde a listagem cross-org (GET /v1/brands) só para quem está na allowlist
// da plataforma e devolve null para os demais, então a própria resposta é o
// teste de permissão. Undefined = ainda carregando: sem acesso até saber.
export function usePlatformDirector(): boolean {
  const { data } = usePlatformDirectorBrands();
  return data !== undefined && data !== null;
}

// Catálogo + permissões para GATEAR TELA (canAccess) fora do beforeLoad da
// guarda de rota — usado pela navegação (sidebar/mobile-nav) e pela jornada
// para decidir o que mostrar. Mesmas chaves de query do bootstrap
// (lib/auth-client.ts), então em operação normal é cache, sem request extra.
export function useAccessSnapshot(): {
  catalog: PermissionCatalog | null;
  permissions: ReadonlySet<string>;
  platformDirector: boolean;
  canCreateBrand: boolean;
  // Cross-marca: flag de plataforma E cliente do tipo 'grupo', já resolvido
  // pelo back (ver AccessMe em api.ts).
  crossBrand: boolean;
  // Papel-base (seller/manager/director) do viewer, para isManager/isDirector —
  // mesma resposta de /v1/access/me, sem request extra (ver comentário acima).
  baseRole: string | null;
  // /v1/access/me ainda não respondeu. Em operação normal já vem resolvido do
  // bootstrap (ver auth-client.ts), mas quem gateia `enabled` de outras
  // queries com o resultado (ex.: settings-shared.tsx) precisa saber da janela
  // intermediária para não montar/remontar conteúdo com o valor errado.
  pending: boolean;
} {
  const { data: me, isPending } = useAccessMe();
  const { data: catalog } = usePermissionCatalog();
  return {
    catalog: catalog ?? null,
    permissions: new Set(me?.permissions ?? []),
    platformDirector: me?.platformDirector ?? false,
    canCreateBrand: me?.canCreateBrand ?? false,
    crossBrand: me?.crossBrand ?? false,
    baseRole: me?.role?.baseRole ?? null,
    pending: isPending,
  };
}

// Cross-marca: o usuário enxerga marca ALHEIA — o consolidado de todas as
// marcas e a listagem cross-org do seletor. Já vem resolvido do back como o par
// (flag de plataforma, cliente do tipo 'grupo'); ver AccessMe em api.ts. Sai da
// mesma resposta de /v1/access/me do bootstrap, então não custa request — ao
// contrário de usePlatformDirector, que usa a própria GET /v1/brands como teste.
export function useCrossBrand(): boolean {
  return useAccessSnapshot().crossBrand;
}

export function useCanCreateBrand(): boolean {
  return useAccessSnapshot().canCreateBrand;
}
